import { enUS, es, ptBR } from "date-fns/locale";
import type { Locale as DateFnsLocale } from "date-fns";

/**
 * Idiomas da interface. O português é a fonte: todo texto nasce nele, e os
 * outros dois são tradução com o mesmo formato (ver `defineCopy`).
 *
 * Variantes escolhidas pelo dono em 2026-09-30: inglês americano e espanhol
 * da Espanha. Qualquer outra variante de espanhol cai no `es-ES`.
 */
export const SUPPORTED_LOCALES = ["pt-BR", "en-US", "es-ES"] as const;

export type Locale = (typeof SUPPORTED_LOCALES)[number];

export const DEFAULT_LOCALE: Locale = "pt-BR";

/**
 * Idioma para quem pede um que a Tãmi não fala (fr, de…): inglês, decisão do
 * dono em 2026-09-30 — quem não lê português lê inglês mais provavelmente.
 */
export const FALLBACK_LOCALE: Locale = "en-US";

const DATE_FNS_LOCALES: Record<Locale, DateFnsLocale> = {
  "pt-BR": ptBR,
  "en-US": enUS,
  "es-ES": es,
};

/**
 * Casa uma tag de idioma (`pt`, `pt-PT`, `es-MX`, `en-GB`, `fr`) com um idioma
 * suportado, pela língua — a região não importa. Tag vazia ou ausente é o
 * idioma padrão (ninguém disse nada); tag de língua que a Tãmi não fala é o
 * fallback.
 */
export function resolveLocale(tag: string | null | undefined): Locale {
  const language = tag?.trim().toLowerCase().split(/[-_]/)[0];
  if (!language) return DEFAULT_LOCALE;
  if (language === "pt") return "pt-BR";
  if (language === "es") return "es-ES";
  if (language === "en") return "en-US";
  return FALLBACK_LOCALE;
}

/**
 * Primeiro idioma suportado de uma lista em ordem de preferência — a do
 * navegador (`navigator.languages`, `Accept-Language`) ou a do aparelho. Uma
 * língua que a Tãmi não fala no topo não esconde uma que ela fala mais abaixo
 * (`fr, es` → `es-ES`); só sem nenhuma conhecida vale o fallback.
 */
export function matchLocale(preferences: readonly string[]): Locale {
  for (const tag of preferences) {
    const language = tag.trim().toLowerCase().split(/[-_;]/)[0];
    if (language === "pt" || language === "es" || language === "en") return resolveLocale(language);
  }
  return preferences.length === 0 ? DEFAULT_LOCALE : FALLBACK_LOCALE;
}

/**
 * Lê um cabeçalho `Accept-Language` (`es-ES,es;q=0.9,en;q=0.8`) na ordem de
 * preferência declarada pelo `q`.
 */
export function parseAcceptLanguage(header: string | null | undefined): string[] {
  if (!header) return [];
  return header
    .split(",")
    .map((part, index) => {
      const [tag = "", ...params] = part.trim().split(";");
      const q = params.map((p) => p.trim()).find((p) => p.startsWith("q="));
      const weight = q ? Number(q.slice(2)) : 1;
      return { tag: tag.trim(), weight: Number.isNaN(weight) ? 0 : weight, index };
    })
    .filter((entry) => entry.tag && entry.tag !== "*" && entry.weight > 0)
    .sort((a, b) => b.weight - a.weight || a.index - b.index)
    .map((entry) => entry.tag);
}

/**
 * O estado mora no `globalThis`, não no módulo. O Next empacota o core uma vez
 * por camada (RSC, SSR, instrumentação), cada uma com a sua cópia do módulo;
 * um `let` aqui daria três idiomas diferentes no mesmo processo. No globalThis
 * quem instala o resolver uma vez vale para todas as cópias.
 */
type LocaleState = { current: Locale; resolver: (() => Locale) | null };

const STATE_KEY = Symbol.for("tami.intl.locale");

function state(): LocaleState {
  const holder = globalThis as unknown as Record<symbol, LocaleState | undefined>;
  holder[STATE_KEY] ??= { current: DEFAULT_LOCALE, resolver: null };
  return holder[STATE_KEY];
}

/**
 * O idioma de agora. Todo texto (`defineCopy`) e toda formatação (`Intl`,
 * `date-fns`) passam por aqui, para não divergirem entre telas nem entre
 * plataformas.
 */
export function getLocale(): Locale {
  const { current, resolver } = state();
  return resolver ? resolver() : current;
}

/**
 * Troca o idioma do processo. Serve para quem atende um usuário só — o app, o
 * desktop, o navegador. O servidor do Next atende todo mundo no mesmo
 * processo e por isso NÃO usa isto: ele instala um `setLocaleResolver` que lê
 * o idioma da requisição.
 */
export function setLocale(locale: Locale): void {
  state().current = locale;
}

/**
 * Substitui a origem do idioma — no servidor do Next, o idioma da requisição
 * em curso. Sem argumento, volta ao idioma do processo (`setLocale`).
 */
export function setLocaleResolver(next?: () => Locale): void {
  state().resolver = next ?? null;
}

/** O locale do `date-fns` do idioma de agora. */
export function getDateFnsLocale(): DateFnsLocale {
  return DATE_FNS_LOCALES[getLocale()];
}
